import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { Customer } from "../components/shared/interfaces/Customer";
import { CustomerDropdown } from "../components/CustomerDropdown";
import RouteBreadcrumbs from "../components/shared/RouteBreadcrumbs";
import { PersistedCompanyDropdown } from "../components/PersistedCompanyDropdown";
import { useCompaniesByCustomerId } from "../components/data/useCompaniesByCustomerId";
import { useSitesByCompanyId } from "../components/data/useSitesByCompanyId";
import { PersistedSiteDropdown } from "../components/PersistedSiteDropdown";
import { Stack } from "@mui/material";
import { ReportListGrid } from "../components/reports/ReportListGrid";
import { ReportsListRequestType } from "../components/shared/apiHandlers/UTP2ReportsAPIs";
import { DataTableMultiSortMetaType, DataTablePFSEvent, DataTableSortOrderType } from "primereact/datatable";
import { LOCALSTORAGE_KEYS } from "../components/shared/constants";
import { INITIAL_LAZY_REPORT_STATE } from "../components/shared/constants/LazyStateReportConstants";

const sortFieldStorageKey = LOCALSTORAGE_KEYS.reportsSortField;
const sortOrderStorageKey = LOCALSTORAGE_KEYS.reportsSortOrder;

export function Reports() {
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | undefined>();
  const [oneCustomerAvailable, setOneCustomerAvailable] = useState(false);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string | undefined>();
  const [selectedSiteName, setSelectedSiteName] = useState<string | undefined>();

  const companies = useCompaniesByCustomerId(selectedCustomer?.id);

  const companyId = useMemo(() => {
    if (companies.data && selectedCompanyName) {
      return companies.data?.find((c) => c.name === selectedCompanyName)?.id;
    }
  }, [companies.data, selectedCompanyName]);

  const sites = useSitesByCompanyId(companyId);

  const siteId = useMemo(() => {
    if (sites.data && selectedSiteName) {
      return sites.data?.find((c) => c.name === selectedSiteName)?.id;
    }
  }, [sites.data, selectedSiteName]);

  const [reportsType, setReportsType] = useState<ReportsListRequestType>();

  const [lazyState, setLazyState] = useState<DataTablePFSEvent>(() => {
    const field = (localStorage.getItem(sortFieldStorageKey) as string) ?? "created";
    const order = (localStorage.getItem(sortOrderStorageKey) as DataTableSortOrderType) ?? -1;
    const sort: DataTableMultiSortMetaType = [{ field: field, order: order }];

    return {
      ...INITIAL_LAZY_REPORT_STATE,
      sortField: field,
      sortOrder: order,
      multiSortMeta: sort,
    };
  });

  const onFilter = (event: DataTablePFSEvent) => {
    setLazyState((prevState) => ({
      ...prevState,
      filters: {
        ...prevState.filters,
        ...event.filters,
      },
    }));
  };

  const onSort = (event: DataTablePFSEvent) => {
    event["page"] = lazyState.page;

    const field = event.multiSortMeta?.at(0)?.field as string;
    const order = event.multiSortMeta?.at(0)?.order ?? 0;

    localStorage.setItem(sortFieldStorageKey, field);

    if (order) {
      localStorage.setItem(sortOrderStorageKey, order.toString());
    }

    event["sortField"] = field !== undefined ? field : "";
    event["sortOrder"] = order;

    setLazyState(event);
  };

  function onPage(event: DataTablePFSEvent) {
    event.page = (event.page ?? 0) + 1;
    setLazyState(event);
  }

  const handleCustomerSelect = useCallback(
    (customer?: Customer) => {
      lazyState["first"] = 0;
      lazyState["page"] = 1;
      setLazyState(lazyState);
      setSelectedCustomer(customer);
    },
    [lazyState],
  );

  const handleSingleCustomerAvailable = useCallback((customer: Customer) => {
    setSelectedCustomer(customer);
    setOneCustomerAvailable(true);
  }, []);

  const handleCompanyChange = useCallback(
    (selectedCompanyName: string) => {
      lazyState["first"] = 0;
      lazyState["page"] = 1;
      setLazyState(lazyState);
      setSelectedCompanyName(selectedCompanyName);
    },
    [lazyState],
  );

  const handleSiteChange = useCallback(
    (selectedSiteName: string) => {
      lazyState["first"] = 0;
      lazyState["page"] = 1;
      setLazyState(lazyState);
      setSelectedSiteName(selectedSiteName);
    },
    [lazyState],
  );

  function determineReportsType(
    selectedCustomer?: Customer,
    companyId?: number,
    siteId?: number,
  ): ReportsListRequestType | undefined {
    if (siteId) {
      return { type: "bySite", siteId };
    } else if (companyId) {
      return { type: "byCompany", companyId };
    } else if (selectedCustomer) {
      return { type: "byCustomer", customerId: selectedCustomer.id };
    }
    return undefined;
  }

  useEffect(() => {
    const newReportsType = determineReportsType(selectedCustomer, companyId, siteId);
    setReportsType(newReportsType);
  }, [selectedCustomer, companyId, siteId]);

  return (
    <React.Fragment>
      <Box marginBottom={2}>
        <RouteBreadcrumbs />
      </Box>

      <Stack direction="row">
        {!oneCustomerAvailable && (
          <CustomerDropdown
            selectedCustomer={selectedCustomer}
            onCustomerSelect={handleCustomerSelect}
            onSingleCustomerAvailable={handleSingleCustomerAvailable}
            showLabel
          />
        )}
        <PersistedCompanyDropdown customerId={selectedCustomer?.id} onCompanyChange={handleCompanyChange} showLabel />
        <PersistedSiteDropdown
          companyId={companyId}
          customerId={selectedCustomer?.id}
          onSiteChange={handleSiteChange}
          showLabel
        />
      </Stack>

      {selectedCustomer && (
        <Box marginTop={2}>
          <ReportListGrid
            customerSelected={selectedCustomer != null}
            customerId={selectedCustomer?.id}
            companyId={companyId}
            siteId={siteId}
            onSort={onSort}
            onFilter={onFilter}
            lazyState={lazyState}
            onPage={onPage}
            reportsType={reportsType}
          />
        </Box>
      )}
    </React.Fragment>
  );
}
